import { ColumnDef } from '@tanstack/react-table'
import classNames from 'classnames'
import { compact } from 'lodash'
import type {
  SmsAgentOpsCounts,
  SmsAiWorkflowRow,
  SmsAgentOpsLocationRow,
  SmsOpsFailedMessage,
  SmsOpsFailedReply,
  SmsOpsOptOut,
  SmsOpsReplyWait,
  SmsOpsTakeover,
  SmsOpsToolErrorTurn,
  SmsStaffTakeoverRow,
  SmsWorkflowRow,
} from 'app/types/sms-agent-ops'
import {
  FAILED_REPLY_COLOR,
  FAILED_REPLY_LABEL,
  FAILURE_REASON_LABEL,
} from 'app/components/ai-agent-sms-ops/smsFailedReplies'
import { FAILURE_GROUP_COLOR, FAILURE_GROUP_LABEL, errorCodeLabel } from 'app/components/ai-agent-sms-ops/smsFailures'
import { METRIC_DEFINITIONS } from 'app/components/ai-agent-sms-ops/smsMetricDefinitions'
import { WAIT_STATUS_COLOR, WAIT_STATUS_LABEL } from 'app/components/ai-agent-sms-ops/smsReplyWaits'
import { SENDER_COLOR, SENDER_LABEL, senderFor } from 'app/components/ai-agent-sms-ops/smsSenders'
import {
  type SmsAgentOpsRates,
  type WorseningMetric,
  ratesFor,
  totalCounts,
  workflowRatesFor,
} from 'app/components/ai-agent-sms-ops/smsAgentOpsMetrics'
import {
  TAKEOVER_CAUSE_COLOR,
  TAKEOVER_CAUSE_LABEL,
  TAKEOVER_TIER_COLOR,
  TAKEOVER_TIER_LABEL,
  takeoverTierFor,
} from 'app/components/ai-agent-sms-ops/smsTakeovers'
import { DateComponent } from 'app/components/DateComponent'
import { AnchorCell } from 'app/components/table/AnchorCell'
import MetricCell from 'app/components/table/MetricCell'
import { Badge } from 'app/components/ui/badge'
import { formatPhoneNumber } from 'app/formatPhoneNumber'

type LocationColumn = ColumnDef<SmsAgentOpsLocationRow, any>

/** Count columns: every count on a location row except the previous-period fields and raw sums. */
type CountKey = Exclude<
  keyof SmsAgentOpsCounts,
  | `aiLatencyMsTotal`
  | `aiCostUsd`
  | `staffResponses`
  | `staffResponseMinutesTotal`
  | `previousAiConversations`
  | `previousTakeovers`
  | `previousInboundThreads`
  | `previousUnansweredThreads`
>

type PercentRate = Extract<
  keyof SmsAgentOpsRates,
  | `takeoverRate`
  | `previousTakeoverRate`
  | `replyRate`
  | `bookingRate`
  | `failureRate`
  | `unansweredRate`
  | `previousUnansweredRate`
  | `toolErrorRate`
>

// Failure and tool error rates sit in the low single digits, so they keep a decimal.
const RATE_DECIMALS: Record<PercentRate, number> = {
  takeoverRate: 0,
  previousTakeoverRate: 0,
  replyRate: 0,
  bookingRate: 0,
  failureRate: 1,
  unansweredRate: 0,
  previousUnansweredRate: 0,
  toolErrorRate: 1,
}

const formatRate = (id: PercentRate, value?: number) =>
  value === undefined ? `-` : `${value.toFixed(RATE_DECIMALS[id])}%`

/** "+3.5 pts", "-12.0 pts", or "-" when either period had nothing to measure. */
const formatChange = (value?: number) => (value === undefined ? `-` : `${value > 0 ? `+` : ``}${value.toFixed(1)} pts`)

const visibleTotals = (rows: { original: SmsAgentOpsLocationRow }[]) => totalCounts(rows.map((row) => row.original))

/** Pinned Location column (clicking filters the page to it), plus Workspace at org level. */
function locationColumns(
  showWorkspace: boolean,
  onLocationClick: (row: SmsAgentOpsLocationRow) => void,
  footer?: string,
): LocationColumn[] {
  return compact([
    {
      accessorFn: (row) => row.locationName,
      id: `location`,
      header: `Location`,
      enableHiding: false,
      enableSorting: true,
      meta: { defaultVisible: true, pinned: true, onClick: onLocationClick },
      footer,
    },
    showWorkspace && {
      accessorFn: (row) => row.workspaceName,
      id: `workspace`,
      header: `Workspace`,
      enableSorting: true,
      meta: { defaultVisible: true },
    },
  ])
}

function countColumn(id: CountKey, header: string): LocationColumn {
  return {
    accessorKey: id,
    id,
    header,
    enableSorting: true,
    cell: (props) => <MetricCell value={props.getValue()} />,
    meta: { defaultVisible: true, infoTooltip: { title: header, message: METRIC_DEFINITIONS[id] } },
    footer: ({ table }) => <MetricCell value={visibleTotals(table.getFilteredRowModel().rows)[id]} />,
  }
}

function rateColumn(id: PercentRate, header: string): LocationColumn {
  return {
    accessorFn: (row) => ratesFor(row)[id],
    id,
    header,
    enableSorting: true,
    sortUndefined: -1,
    cell: (props) => formatRate(id, props.getValue()),
    meta: { defaultVisible: true, infoTooltip: { title: header, message: METRIC_DEFINITIONS[id] } },
    // Weighted across the visible rows (total ÷ total), not an average of row rates.
    footer: ({ table }) => formatRate(id, visibleTotals(table.getFilteredRowModel().rows)[id]),
  }
}

/** A rise colors the cell red: for takeovers and unanswered threads, up is worse. */
function changeColumn(id: `takeoverRateChange` | `unansweredRateChange`, header: string): LocationColumn {
  return {
    accessorFn: (row) => ratesFor(row)[id],
    id,
    header,
    enableSorting: true,
    sortUndefined: -1,
    cell: (props) => (
      <span className={classNames({ 'text-red-600': (props.getValue() ?? 0) > 0 })}>
        {formatChange(props.getValue())}
      </span>
    ),
    meta: { defaultVisible: true, infoTooltip: { title: header, message: METRIC_DEFINITIONS[id] } },
    footer: ({ table }) => formatChange(visibleTotals(table.getFilteredRowModel().rows)[id]),
  }
}

/** A derived average (seconds, minutes, dollars) with its weighted footer. */
function averageColumn(
  id: `avgAiLatencySeconds` | `avgStaffResponseMinutes` | `costPerConversation`,
  header: string,
  definition: string,
  format: (value: number) => string,
): LocationColumn {
  const render = (value?: number) => (value === undefined ? `-` : format(value))
  return {
    accessorFn: (row) => ratesFor(row)[id],
    id,
    header,
    enableSorting: true,
    sortUndefined: -1,
    cell: (props) => render(props.getValue()),
    meta: { defaultVisible: true, infoTooltip: { title: header, message: definition } },
    footer: ({ table }) => render(visibleTotals(table.getFilteredRowModel().rows)[id]),
  }
}

export const formatSeconds = (seconds: number) => `${seconds.toFixed(1)}s`
/** "12m", "3h 5m", or "47d 2h" once a wait runs past a day. */
export function formatMinutes(minutes: number): string {
  if (minutes >= 1440) return `${Math.floor(minutes / 1440)}d ${Math.floor((minutes % 1440) / 60)}h`
  if (minutes >= 60) return `${Math.floor(minutes / 60)}h ${Math.round(minutes % 60)}m`
  return `${Math.round(minutes)}m`
}
export const formatUsd = (usd: number) => `$${usd.toFixed(3)}`

const smsAgentColumn: LocationColumn = {
  accessorFn: (row) => row.hasSmsAgent,
  id: `smsAgent`,
  header: `SMS Agent`,
  enableSorting: true,
  cell: (props) => (
    <Badge label={props.getValue() ? `SMS Agent` : `None`} color={props.getValue() ? `purple` : `gray`} />
  ),
  meta: { defaultVisible: true },
}

type LocationColumnsOptions = {
  /** Org-level view spans workspaces, so it names each location's workspace. */
  showWorkspace: boolean
  onLocationClick: (row: SmsAgentOpsLocationRow) => void
}

export function smsAgentOpsLocationColumns({
  showWorkspace,
  onLocationClick,
}: LocationColumnsOptions): LocationColumn[] {
  return [
    ...locationColumns(showWorkspace, onLocationClick, `Total`),
    smsAgentColumn,
    countColumn(`inbound`, `Inbound`),
    countColumn(`outbound`, `Outbound`),
    countColumn(`agentTexts`, `AI Agent Texts`),
    countColumn(`workflowTexts`, `Workflow Texts`),
    countColumn(`staffTexts`, `Staff Texts`),
    countColumn(`aiConversations`, `AI Conversations`),
    rateColumn(`replyRate`, `Reply Rate`),
    countColumn(`takeovers`, `Takeovers`),
    rateColumn(`takeoverRate`, `Takeover Rate`),
    countColumn(`bookings`, `SMS Bookings`),
    countColumn(`failedTexts`, `Failed Texts`),
    rateColumn(`failureRate`, `Failure Rate`),
    countColumn(`optOuts`, `Opt-Outs`),
    countColumn(`unansweredThreads`, `Unanswered`),
    rateColumn(`unansweredRate`, `Unanswered Rate`),
    averageColumn(`avgStaffResponseMinutes`, `Avg Staff Response`, METRIC_DEFINITIONS.avgStaffResponse, formatMinutes),
    averageColumn(`avgAiLatencySeconds`, `Avg AI Latency`, METRIC_DEFINITIONS.avgAiLatency, formatSeconds),
    rateColumn(`toolErrorRate`, `Tool Error Rate`),
    averageColumn(`costPerConversation`, `Cost / Conversation`, METRIC_DEFINITIONS.costPerConversation, formatUsd),
    {
      accessorFn: (row) => row.lastAgentTextDate,
      id: `lastAgentText`,
      header: `Last AI Text`,
      enableSorting: true,
      sortUndefined: -1,
      cell: (props) => <DateComponent value={props.getValue()} dateOnly />,
      meta: { defaultVisible: true },
    },
  ]
}

/** Takeovers by Location: SMS Agent locations, worst first, with a tier badge (plan D10). */
export function smsAgentOpsTakeoverColumns({
  showWorkspace,
  onLocationClick,
}: LocationColumnsOptions): LocationColumn[] {
  return [
    ...locationColumns(showWorkspace, onLocationClick),
    {
      accessorFn: (row) => takeoverTierFor(row),
      id: `tier`,
      header: `Status`,
      enableSorting: false,
      cell: (props) => (
        <Badge
          label={TAKEOVER_TIER_LABEL[props.getValue() as keyof typeof TAKEOVER_TIER_LABEL]}
          color={TAKEOVER_TIER_COLOR[props.getValue() as keyof typeof TAKEOVER_TIER_COLOR]}
        />
      ),
      meta: { defaultVisible: true, infoTooltip: { title: `Status`, message: METRIC_DEFINITIONS.takeoverRate } },
    },
    rateColumn(`takeoverRate`, `Takeover Rate`),
    countColumn(`takeovers`, `Takeovers`),
    countColumn(`aiConversations`, `AI Conversations`),
    countColumn(`staffReplyTakeovers`, TAKEOVER_CAUSE_LABEL.staffReply),
    countColumn(`manualToggleTakeovers`, TAKEOVER_CAUSE_LABEL.manualToggle),
    countColumn(`optOutTakeovers`, TAKEOVER_CAUSE_LABEL.optOut),
    rateColumn(`previousTakeoverRate`, `Previous Takeover Rate`),
    changeColumn(`takeoverRateChange`, `Takeover Rate Change`),
  ]
}

/** Getting Worse: current vs previous period for the chosen rate (plan D14). */
export function smsAgentOpsWorseningColumns({
  showWorkspace,
  onLocationClick,
  metric,
}: LocationColumnsOptions & { metric: WorseningMetric }): LocationColumn[] {
  const location = locationColumns(showWorkspace, onLocationClick)
  if (metric === `takeoverRate`) {
    return [
      ...location,
      rateColumn(`takeoverRate`, `Takeover Rate`),
      rateColumn(`previousTakeoverRate`, `Previous Takeover Rate`),
      changeColumn(`takeoverRateChange`, `Takeover Rate Change`),
      countColumn(`aiConversations`, `AI Conversations`),
    ]
  }
  return [
    ...location,
    rateColumn(`unansweredRate`, `Unanswered Rate`),
    rateColumn(`previousUnansweredRate`, `Previous Unanswered Rate`),
    changeColumn(`unansweredRateChange`, `Unanswered Rate Change`),
    countColumn(`inboundThreads`, `Inbound Threads`),
  ]
}

export function smsStaffTakeoverColumns(): ColumnDef<SmsStaffTakeoverRow, any>[] {
  const count = (
    id: `takeovers` | `staffReplyTakeovers` | `manualToggleTakeovers`,
    header: string,
    definition: string,
  ) =>
    ({
      accessorKey: id,
      id,
      header,
      enableSorting: true,
      cell: (props) => <MetricCell value={props.getValue()} />,
      meta: { defaultVisible: true, infoTooltip: { title: header, message: definition } },
    }) satisfies ColumnDef<SmsStaffTakeoverRow, any>
  return [
    {
      accessorKey: `userName`,
      id: `userName`,
      header: `Staff`,
      enableSorting: true,
      meta: { defaultVisible: true, pinned: true },
    },
    {
      accessorKey: `locationName`,
      id: `locationName`,
      header: `Location`,
      enableSorting: true,
      meta: { defaultVisible: true },
    },
    count(`takeovers`, `Takeovers`, `Staff reply and turned-off takeovers by this person. Opt-outs have no person.`),
    count(`staffReplyTakeovers`, TAKEOVER_CAUSE_LABEL.staffReply, METRIC_DEFINITIONS.staffReplyTakeovers),
    count(`manualToggleTakeovers`, TAKEOVER_CAUSE_LABEL.manualToggle, METRIC_DEFINITIONS.manualToggleTakeovers),
    {
      accessorKey: `lastTakeoverDate`,
      id: `lastTakeoverDate`,
      header: `Last Takeover`,
      enableSorting: true,
      sortUndefined: -1,
      cell: (props) => <DateComponent value={props.getValue()} dateOnly />,
      meta: { defaultVisible: true },
    },
  ]
}

/** Workflows: one row per workflow, with the location that runs it (plan D22); clicking it filters the page. */
export function smsWorkflowColumns({
  onLocationClick,
}: {
  onLocationClick: (row: SmsWorkflowRow) => void
}): ColumnDef<SmsWorkflowRow, any>[] {
  const count = (id: `textsSent` | `threads` | `optOuts` | `failedTexts`, header: string, definition: string) =>
    ({
      accessorKey: id,
      id,
      header,
      enableSorting: true,
      cell: (props) => <MetricCell value={props.getValue()} />,
      meta: { defaultVisible: true, infoTooltip: { title: header, message: definition } },
    }) satisfies ColumnDef<SmsWorkflowRow, any>
  const rate = (id: `replyRate` | `optOutRate` | `failureRate`, header: string, definition: string, decimals: number) =>
    ({
      accessorFn: (row) => workflowRatesFor(row)[id],
      id,
      header,
      enableSorting: true,
      sortUndefined: -1,
      cell: (props) => (props.getValue() === undefined ? `-` : `${(props.getValue() as number).toFixed(decimals)}%`),
      meta: { defaultVisible: true, infoTooltip: { title: header, message: definition } },
    }) satisfies ColumnDef<SmsWorkflowRow, any>
  return [
    {
      accessorKey: `workflowName`,
      id: `workflowName`,
      header: `Workflow`,
      enableSorting: true,
      meta: { defaultVisible: true, pinned: true },
    },
    {
      accessorFn: (row) => row.locationName ?? `All locations`,
      id: `location`,
      header: `Location`,
      enableSorting: true,
      meta: { defaultVisible: true, onClick: onLocationClick },
    },
    count(`textsSent`, `Texts Sent`, `Texts this workflow's steps sent.`),
    count(`threads`, `Threads`, `Distinct threads the workflow texted.`),
    rate(`replyRate`, `Reply Rate`, `Threads where the patient replied ÷ threads texted.`, 0),
    count(`optOuts`, `Opt-Outs`, `Threads that opted out after a text from this workflow.`),
    rate(
      `optOutRate`,
      `Opt-Out Rate`,
      `Opt-outs ÷ threads texted. A high rate means the workflow is annoying patients.`,
      1,
    ),
    count(`failedTexts`, `Failed Texts`, METRIC_DEFINITIONS.failedTexts),
    rate(`failureRate`, `Failure Rate`, `Failed texts ÷ texts sent.`, 1),
  ]
}

type ListColumnsOptions = { navigate: (href: string) => void }

/** The inbox link for a thread; `URLSearchParams` encodes the ids, so API values can't add parameters. */
export function inboxThreadHref(locationId?: string | null, threadId?: string | null): string | undefined {
  if (!threadId) return undefined
  return `/inbox?${new URLSearchParams({ locationId: locationId ?? ``, tab: threadId })}`
}

/** Date, Location, then the list's own columns, then (given a thread) an Open in Inbox link. */
function listColumns<T extends { locationId?: string | null; locationName: string }>(
  { navigate }: ListColumnsOptions,
  dateAccessor: (row: T) => string | null | undefined,
  middle: ColumnDef<T, any>[],
  threadAccessor?: (row: T) => string | null | undefined,
): ColumnDef<T, any>[] {
  return compact([
    {
      accessorFn: dateAccessor,
      id: `date`,
      header: `Date`,
      enableSorting: true,
      // Instants from the API; without `useLocalUtc` they render in UTC.
      cell: (props) => <DateComponent value={props.getValue()} useLocalUtc />,
      meta: { defaultVisible: true },
    },
    {
      accessorFn: (row) => row.locationName,
      id: `location`,
      header: `Location`,
      enableSorting: true,
      meta: { defaultVisible: true },
    },
    ...middle,
    threadAccessor && threadColumn(navigate, threadAccessor),
  ])
}

/** An Open in Inbox link to the row's thread, or a dash when it has none. */
function threadColumn<T extends { locationId?: string | null }>(
  navigate: ListColumnsOptions[`navigate`],
  threadAccessor: (row: T) => string | null | undefined,
): ColumnDef<T, any> {
  return {
    accessorFn: (row: T) => inboxThreadHref(row.locationId, threadAccessor(row)),
    id: `thread`,
    header: `Thread`,
    enableSorting: false,
    meta: {
      defaultVisible: true,
      ControlledComponent: (row?: T) => {
        const href = row && inboxThreadHref(row.locationId, threadAccessor(row))
        if (!href) return <span className="px-2 text-subtle">-</span>
        return (
          <AnchorCell href={href} clickHandler={() => navigate(href)}>
            <span>Open in Inbox</span>
          </AnchorCell>
        )
      },
    },
  }
}

export function smsTakeoverListColumns(options: ListColumnsOptions): ColumnDef<SmsOpsTakeover, any>[] {
  return listColumns<SmsOpsTakeover>(
    options,
    (row) => row.eventDate,
    [
      {
        accessorFn: (row) => TAKEOVER_CAUSE_LABEL[row.cause],
        id: `cause`,
        header: `Cause`,
        enableSorting: true,
        meta: {
          defaultVisible: true,
          ControlledComponent: (row) =>
            row ? (
              <div className="px-2">
                <Badge label={TAKEOVER_CAUSE_LABEL[row.cause]} color={TAKEOVER_CAUSE_COLOR[row.cause]} />
              </div>
            ) : (
              <></>
            ),
        },
      },
      {
        accessorFn: (row) => row.userName ?? (row.userId ? `Unknown user` : `-`),
        id: `staff`,
        header: `Staff`,
        enableSorting: true,
        meta: { defaultVisible: true },
      },
    ],
    // A Thread Event's `typeId` is the thread.
    (row) => row.typeId,
  )
}

export function smsFailedTextColumns(options: ListColumnsOptions): ColumnDef<SmsOpsFailedMessage, any>[] {
  return listColumns<SmsOpsFailedMessage>(
    options,
    (row) => row.createdDate,
    [
      {
        accessorFn: (row) => FAILURE_GROUP_LABEL[row.failureGroup],
        id: `failureGroup`,
        header: `Failure`,
        enableSorting: true,
        meta: {
          defaultVisible: true,
          ControlledComponent: (row) =>
            row ? (
              <div className="px-2">
                <Badge label={FAILURE_GROUP_LABEL[row.failureGroup]} color={FAILURE_GROUP_COLOR[row.failureGroup]} />
              </div>
            ) : (
              <></>
            ),
        },
      },
      {
        accessorFn: (row) => errorCodeLabel(row.errorCode),
        id: `errorCode`,
        header: `Error`,
        enableSorting: true,
        meta: { defaultVisible: true, tooltipText: (row?: SmsOpsFailedMessage) => row?.errorMessage ?? `` },
      },
      {
        accessorFn: (row) => {
          const sender = senderFor(row)
          return sender ? SENDER_LABEL[sender] : `-`
        },
        id: `sender`,
        header: `Sender`,
        enableSorting: true,
        meta: {
          defaultVisible: true,
          ControlledComponent: (row) => {
            const sender = row && senderFor(row)
            return sender ? (
              <div className="px-2">
                <Badge label={SENDER_LABEL[sender]} color={SENDER_COLOR[sender]} />
              </div>
            ) : (
              <></>
            )
          },
        },
      },
      {
        accessorFn: (row) => formatPhoneNumber(row.to) ?? row.to,
        id: `to`,
        header: `To`,
        enableSorting: true,
        meta: { defaultVisible: true },
      },
      {
        accessorKey: `provider`,
        id: `provider`,
        header: `Provider`,
        enableSorting: true,
        meta: { defaultVisible: true },
      },
    ],
    (row) => row.threadId,
  )
}

/** An opt-out is keyed by number, not thread, so the list has no inbox link. */
export function smsOptOutColumns(options: ListColumnsOptions): ColumnDef<SmsOpsOptOut, any>[] {
  return listColumns<SmsOpsOptOut>(options, (row) => row.createdDate, [
    {
      accessorFn: (row) => formatPhoneNumber(row.key) ?? row.key,
      id: `number`,
      header: `Number`,
      enableSorting: true,
      meta: { defaultVisible: true },
    },
  ])
}

/**
 * The pinned Name column: opens the contact's drawer on its Inbox tab in the row's workspace
 * (`drawerTab` on the Contacts page), or plain text when the row has no contact.
 */
function contactColumn<T extends { contactId?: string; contactName?: string; workspaceId: string }>(
  navigate: ListColumnsOptions[`navigate`],
): ColumnDef<T, any> {
  return {
    accessorFn: (row: T) => row.contactName ?? `-`,
    id: `contact`,
    header: `Name`,
    enableSorting: true,
    meta: {
      defaultVisible: true,
      pinned: true,
      ControlledComponent: (row?: T) => {
        if (!row?.contactId) return <span className="px-2">{row?.contactName ?? `-`}</span>
        const href = `/contacts?${new URLSearchParams({ drawer: row.contactId, wId: row.workspaceId, drawerTab: `inbox` })}`
        return (
          <AnchorCell href={href} clickHandler={() => navigate(href)}>
            <span>{row.contactName || `Unnamed contact`}</span>
          </AnchorCell>
        )
      },
    },
  }
}

/**
 * Waiting on a Reply (plan D37): status, who, where, and for how long. Workspace shows only at
 * org level, as on Voice Ops; every row is in the session's org. The name opens the
 * contact's drawer on its Inbox tab in the thread's workspace (`drawerTab` on the Contacts page).
 */
export function smsReplyWaitColumns({
  navigate,
  showWorkspace,
}: ListColumnsOptions & { showWorkspace: boolean }): ColumnDef<SmsOpsReplyWait, any>[] {
  return compact([
    {
      accessorFn: (row) => WAIT_STATUS_LABEL[row.status],
      id: `status`,
      header: `Status`,
      enableSorting: true,
      meta: {
        defaultVisible: true,
        ControlledComponent: (row?: SmsOpsReplyWait) =>
          row ? (
            <div className="px-2">
              <Badge label={WAIT_STATUS_LABEL[row.status]} color={WAIT_STATUS_COLOR[row.status]} />
            </div>
          ) : (
            <></>
          ),
      },
    },
    contactColumn<SmsOpsReplyWait>(navigate),
    {
      accessorFn: (row) => row.phone,
      id: `phone`,
      header: `Phone`,
      enableSorting: false,
      cell: (props) => formatPhoneNumber(props.getValue()) ?? `-`,
      meta: { defaultVisible: true },
    },
    {
      accessorFn: (row) => row.textedDate,
      id: `texted`,
      header: `Patient Texted`,
      enableSorting: true,
      cell: (props) => <DateComponent value={props.getValue()} useLocalUtc />,
      meta: {
        defaultVisible: true,
        infoTooltip: { title: `Patient Texted`, message: `When the patient's text that waited came in.` },
      },
    },
    {
      accessorFn: (row) => row.waitedMinutes,
      id: `waited`,
      header: `Waited`,
      enableSorting: true,
      cell: (props) => formatMinutes(props.getValue()),
      meta: {
        defaultVisible: true,
        infoTooltip: {
          title: `Waited`,
          message: `Waiting: time so far, as of when this page loaded. Replied Late: time until the AI replied.`,
        },
      },
    },
    {
      accessorFn: (row) => row.locationName,
      id: `location`,
      header: `Location`,
      enableSorting: true,
      meta: { defaultVisible: true },
    },
    showWorkspace && {
      accessorFn: (row: SmsOpsReplyWait) => row.workspaceName ?? `-`,
      id: `workspace`,
      header: `Workspace`,
      enableSorting: true,
      meta: { defaultVisible: true },
    },
    {
      accessorFn: (row) => row.aiLastSpoke,
      id: `aiLastSpoke`,
      header: `AI Last Spoke`,
      enableSorting: true,
      sortUndefined: -1,
      cell: (props) => <DateComponent value={props.getValue()} useLocalUtc />,
      meta: { defaultVisible: true },
    },
    threadColumn<SmsOpsReplyWait>(navigate, (row) => row.threadId),
  ])
}

/**
 * Failed AI Replies (plan D39): one row per patient text the AI failed to answer, why, and
 * whether a later attempt got through. Workspace shows only at org level, as elsewhere.
 */
export function smsFailedReplyColumns({
  navigate,
  showWorkspace,
}: ListColumnsOptions & { showWorkspace: boolean }): ColumnDef<SmsOpsFailedReply, any>[] {
  return compact([
    {
      accessorFn: (row) => FAILED_REPLY_LABEL[row.status],
      id: `status`,
      header: `Status`,
      enableSorting: true,
      // Wide enough for the "Answered later" badge, so it never spills into Name.
      minSize: 130,
      meta: {
        defaultVisible: true,
        ControlledComponent: (row?: SmsOpsFailedReply) =>
          row ? (
            <div className="px-2">
              <Badge label={FAILED_REPLY_LABEL[row.status]} color={FAILED_REPLY_COLOR[row.status]} />
            </div>
          ) : (
            <></>
          ),
      },
    },
    contactColumn<SmsOpsFailedReply>(navigate),
    {
      accessorFn: (row) => row.firstFailedAt,
      id: `failedAt`,
      header: `Failed At`,
      enableSorting: true,
      cell: (props) => <DateComponent value={props.getValue()} useLocalUtc />,
      meta: {
        defaultVisible: true,
        infoTooltip: { title: `Failed At`, message: `When the AI's first attempt to answer this patient text failed.` },
      },
    },
    {
      accessorFn: (row) => (row.reason && FAILURE_REASON_LABEL[row.reason]) || `Unknown error`,
      id: `reason`,
      header: `Reason`,
      enableSorting: true,
      meta: { defaultVisible: true },
    },
    {
      accessorFn: (row) => row.failureMessage ?? `-`,
      id: `error`,
      header: `Error`,
      enableSorting: false,
      meta: {
        defaultVisible: true,
        infoTooltip: {
          title: `Error`,
          message: `The error from the latest failed attempt, as the system recorded it.`,
        },
      },
    },
    {
      accessorFn: (row) => row.attempts,
      id: `attempts`,
      header: `Attempts`,
      enableSorting: true,
      meta: {
        defaultVisible: true,
        infoTooltip: {
          title: `Attempts`,
          message: `Failed attempts at this text. A failed reply is retried automatically, and each failed retry counts.`,
        },
      },
    },
    {
      accessorFn: (row) => row.answeredAfterMinutes,
      id: `answeredAfter`,
      header: `Answered After`,
      enableSorting: true,
      sortUndefined: -1,
      cell: (props) => (props.getValue() === undefined ? `-` : formatMinutes(props.getValue())),
      meta: {
        defaultVisible: true,
        infoTooltip: {
          title: `Answered After`,
          message: `Time from the last failed attempt to the next AI reply on the thread.`,
        },
      },
    },
    {
      accessorFn: (row) => row.locationName,
      id: `location`,
      header: `Location`,
      enableSorting: true,
      meta: { defaultVisible: true },
    },
    showWorkspace && {
      accessorFn: (row: SmsOpsFailedReply) => row.workspaceName ?? `-`,
      id: `workspace`,
      header: `Workspace`,
      enableSorting: true,
      meta: { defaultVisible: true },
    },
    threadColumn<SmsOpsFailedReply>(navigate, (row) => row.threadId),
  ])
}

export function smsToolErrorColumns(
  options: ListColumnsOptions & { onTurnClick: (turn: SmsOpsToolErrorTurn) => void },
): ColumnDef<SmsOpsToolErrorTurn, any>[] {
  return listColumns<SmsOpsToolErrorTurn>(
    options,
    (row) => row.createdDate,
    [
      {
        accessorFn: (row) =>
          compact(
            row.steps?.flatMap((step) => step.toolResults?.filter((r) => r.isError).map((r) => r.toolName) ?? []),
          ).join(`, `),
        id: `tools`,
        header: `Failed Tools`,
        enableSorting: true,
        meta: { defaultVisible: true, onClick: options.onTurnClick },
      },
      { accessorKey: `model`, id: `model`, header: `Model`, enableSorting: true, meta: { defaultVisible: true } },
      {
        accessorFn: (row) => (row.latencyMs === null ? undefined : row.latencyMs / 1000),
        id: `latency`,
        header: `Latency`,
        enableSorting: true,
        sortUndefined: -1,
        cell: (props) => (props.getValue() === undefined ? `-` : formatSeconds(props.getValue())),
        meta: { defaultVisible: true },
      },
    ],
    (row) => row.threadId,
  )
}

/** The Agents SMS dashboard's By Workflow table, built from the AI agent's live texts. */
export function smsAiWorkflowColumns(): ColumnDef<SmsAiWorkflowRow, any>[] {
  const count = (id: `aiTexts` | `conversations` | `takeovers`, header: string, definition: string) =>
    ({
      accessorKey: id,
      id,
      header,
      enableSorting: true,
      cell: (props) => <MetricCell value={props.getValue()} />,
      meta: { defaultVisible: true, infoTooltip: { title: header, message: definition } },
    }) satisfies ColumnDef<SmsAiWorkflowRow, any>
  return [
    {
      accessorKey: `workflowName`,
      id: `workflowName`,
      header: `Workflow`,
      enableSorting: true,
      meta: { defaultVisible: true, pinned: true },
    },
    count(`aiTexts`, `AI Texts`, `Texts the AI agent sent while this workflow had the thread.`),
    count(`conversations`, `Conversations`, `Distinct threads the AI agent texted for this workflow.`),
    count(`takeovers`, `Takeovers`, `Of those threads, how many staff took over from the AI agent.`),
  ]
}
